import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'attributionGenerated'
	| 'attributionAnswer'
	| 'panelHeading'
	| 'closePanel'
	| 'viewExpert'
	| 'feedbackPrompt'
	| 'feedbackHelpfulLabel'
	| 'feedbackNotHelpfulLabel'
	| 'feedbackHeadingPositive'
	| 'feedbackHeadingNegative'
	| 'feedbackItWasLabel'
	| 'feedbackSubmitLabel'
	| 'feedbackCloseLabel'
	| 'feedbackConfirmation'
	| 'feedbackAdditionalLabel'
	| 'feedbackAdditionalPlaceholder'
	| 'feedbackAdditionalRequired'
	| 'categoryUpToDate'
	| 'categoryAccurate'
	| 'categoryHelpful'
	| 'categoryWellSourced'
	| 'categoryOutOfDate'
	| 'categoryInaccurate'
	| 'categoryNotHelpful'
	| 'categoryPoorlySourced'
	| 'categoryOther';

const messages: Record<MessageKeys, { id: string; defaultMessage: string; description: string }> =
	defineMessages({
		attributionGenerated: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionGenerated',
			defaultMessage: 'Verified across <trigger>high confidence knowledge</trigger>',
			description:
				'Chat variant: attribution line shown above an AI answer. The text wrapped in <trigger> is a clickable link that opens the combined sources and contributors panel.',
		},
		attributionAnswer: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionAnswer',
			defaultMessage:
				'<bold>Answer</bold> verified across <trigger>high confidence knowledge</trigger>',
			description:
				'SAIN variant: full attribution sentence shown in the answer card heading. <bold> wraps the word "Answer" which is rendered in bold. The text wrapped in <trigger> is a clickable link that opens the combined sources and contributors panel.',
		},
		panelHeading: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.panelHeading',
			defaultMessage: 'High confidence knowledge',
			description: 'Heading of the combined sources and contributors panel.',
		},
		closePanel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.closePanel',
			defaultMessage: 'Close',
			description: 'Accessible label for the button that dismisses the attribution detail panel.',
		},
		viewExpert: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.viewExpert',
			defaultMessage: 'View {name}',
			description:
				'Accessible label for the button that navigates to an expert contributor profile. {name} is the expert name.',
		},
		feedbackPrompt: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackPrompt',
			defaultMessage: 'Was this high confidence knowledge helpful?',
			description:
				'Prompt shown at the bottom of the panel asking the user to rate the high confidence knowledge with thumbs up or down.',
		},
		feedbackHelpfulLabel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackHelpfulLabel',
			defaultMessage: 'Helpful',
			description: 'Accessible label for the thumbs up (positive feedback) button.',
		},
		feedbackNotHelpfulLabel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackNotHelpfulLabel',
			defaultMessage: 'Not helpful',
			description: 'Accessible label for the thumbs down (negative feedback) button.',
		},
		feedbackHeadingPositive: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackHeadingPositive',
			defaultMessage: 'What worked about this response?',
			description: 'Heading shown above the feedback form after the user selects thumbs up.',
		},
		feedbackHeadingNegative: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackHeadingNegative',
			defaultMessage: "What didn't work about these sources?",
			description: 'Heading shown above the feedback form after the user selects thumbs down.',
		},
		feedbackItWasLabel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackItWasLabel',
			defaultMessage: 'It was...',
			description:
				'Subtle caption shown below the feedback question, introducing the list of selectable reason chips.',
		},
		feedbackSubmitLabel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackSubmitLabel',
			defaultMessage: 'Submit feedback',
			description: 'Label for the button that submits the feedback form.',
		},
		feedbackCloseLabel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackCloseLabel',
			defaultMessage: 'Close feedback form',
			description: 'Accessible label for the button that closes the feedback form.',
		},
		feedbackConfirmation: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackConfirmation',
			defaultMessage: 'Thanks for your feedback',
			description: 'Confirmation message shown after the user submits feedback.',
		},
		feedbackAdditionalLabel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackAdditionalLabel',
			defaultMessage: 'Additional feedback',
			description: 'Label for the additional feedback free-text area.',
		},
		feedbackAdditionalPlaceholder: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackAdditionalPlaceholder',
			defaultMessage: 'Tell us more',
			description: 'Placeholder for the additional feedback free-text area.',
		},
		feedbackAdditionalRequired: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.feedbackAdditionalRequired',
			defaultMessage: 'Please provide additional details when selecting "Other"',
			description:
				'Error message shown when the user selects the "Other" reason without providing details.',
		},
		categoryUpToDate: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryUpToDate',
			defaultMessage: 'Up to date',
			description: 'Positive feedback reason: the sources were up to date.',
		},
		categoryAccurate: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryAccurate',
			defaultMessage: 'Accurate',
			description: 'Positive feedback reason: the answer was accurate.',
		},
		categoryHelpful: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryHelpful',
			defaultMessage: 'Helpful',
			description: 'Positive feedback reason: the answer was helpful.',
		},
		categoryWellSourced: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryWellSourced',
			defaultMessage: 'Well-sourced',
			description: 'Positive feedback reason: the answer was well sourced.',
		},
		categoryOutOfDate: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryOutOfDate',
			defaultMessage: 'Out of date',
			description: 'Negative feedback reason: the sources were out of date.',
		},
		categoryInaccurate: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryInaccurate',
			defaultMessage: 'Inaccurate',
			description: 'Negative feedback reason: the answer was inaccurate.',
		},
		categoryNotHelpful: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryNotHelpful',
			defaultMessage: 'Not helpful',
			description: 'Negative feedback reason: the answer was not helpful.',
		},
		categoryPoorlySourced: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryPoorlySourced',
			defaultMessage: 'Poorly-sourced',
			description: 'Negative feedback reason: the answer was poorly sourced.',
		},
		categoryOther: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.categoryOther',
			defaultMessage: 'Other',
			description: 'Feedback reason for any other issue not listed.',
		},
	});

export default messages;
